// Canned dev-only mock WS server. Activated with `?mock=1` in the URL.
// Streams a scripted sequence of OutMsg frames so the browser source can be developed
// without the Stream A backend running.

import type { OutMsg } from '@sg/shared';

type Listener = (m: OutMsg) => void;

function now(): number {
  return Date.now();
}

export interface MockBus {
  emit: (m: OutMsg) => void;
  addListener: (l: Listener) => () => void;
  runScript: () => void;
}

export function createMockBus(): MockBus {
  const listeners = new Set<Listener>();
  return {
    emit(m) {
      for (const l of listeners) l(m);
    },
    addListener(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    runScript() {
      // Placeholder canned sequence: snapshot -> category poll -> clue_arm -> clue_reveal ->
      // grace -> answer reveal -> score update.
      const t0 = now();
      const emit = (m: OutMsg, delay: number): void => {
        setTimeout(() => {
          for (const l of listeners) l(m);
        }, delay);
      };
      emit(
        {
          type: 'state_snapshot',
          state: {
            tenant_id: 'mock',
            round: 'r1',
            state: 'CATEGORY_POLL',
            server_ts: t0,
            broadcast_delay_seconds: 5,
            leaderboard_top10: [],
          },
        },
        50,
      );
      emit(
        {
          type: 'poll_open',
          phase: 'category',
          options: [
            { key: 'SCIENCE', label: 'Science' },
            { key: 'HISTORY', label: 'History' },
            { key: 'GEOGRAPHY', label: 'Geography' },
            { key: 'MOVIES', label: 'Movies' },
            { key: 'SPORTS', label: 'Sports' },
            { key: 'TECH', label: 'Tech' },
          ],
          ends_at_ms: t0 + 15_000,
        },
        150,
      );
      emit(
        {
          type: 'poll_tally',
          phase: 'category',
          tallies: { SCIENCE: 3, HISTORY: 1, MOVIES: 7 },
        },
        3_000,
      );
      emit({ type: 'poll_close', phase: 'category', winner: 'MOVIES' }, 4_000);
      emit(
        {
          type: 'clue_arm',
          cluePreview: { id: 'x', category: 'MOVIES', value: 600, round: 1 },
          reveal_at_ms: t0 + 6_500,
        },
        5_000,
      );
      emit(
        {
          type: 'clue_reveal',
          clue: {
            id: 'x',
            category: 'MOVIES',
            round: 1,
            value: 600,
            prompt: 'This 1972 film features the line "I\'m gonna make him an offer he can\'t refuse."',
            display_answer: 'The Godfather',
          },
          reveal_at_ms: t0 + 6_500,
          hard_end_ms: t0 + 16_500,
          grace_end_ms: t0 + 21_500,
          broadcast_delay_seconds: 5,
        },
        6_000,
      );
      emit({ type: 'grace_begin', grace_end_ms: t0 + 21_500 }, 16_500);
      emit(
        {
          type: 'answer_reveal',
          display_answer: 'The Godfather',
          top_scorers: [
            { player_id: 1, display_name: 'Alice', avatar_seed: 'Alice', points: 480, t_ms: 400 },
            { player_id: 2, display_name: 'Bob', avatar_seed: 'Bob', points: 360, t_ms: 800 },
          ],
        },
        22_000,
      );
      emit(
        {
          type: 'score_update',
          deltas: [
            { player_id: 1, display_name: 'Alice', delta: 480, new_total: 1240 },
            { player_id: 2, display_name: 'Bob', delta: 360, new_total: 720 },
          ],
          leaderboard_top10: [
            {
              rank: 1,
              player_id: 1,
              display_name: 'Alice',
              avatar_seed: 'Alice',
              total_points: 1240,
              games_played: 1,
              games_won: 0,
            },
            {
              rank: 2,
              player_id: 2,
              display_name: 'Bob',
              avatar_seed: 'Bob',
              total_points: 720,
              games_played: 1,
              games_won: 0,
            },
          ],
        },
        26_000,
      );
    },
  };
}
